import { ActionIcon, Badge, Box, Group, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { useEffect, useRef } from "react";
import type { Ref } from "react";
import { useTranslation } from "react-i18next";

import type { CustomerSafeTicket } from "@msc/shared";

import { absoluteDate, daysOpen, relativeTime } from "../../lib/customerView/relativeTime.js";
import { WAITING_STATUS, type TicketGroup } from "../../lib/customerView/ticketView.js";
import { AssigneeBadge } from "./AssigneeBadge.js";
import { PriorityChip } from "./PriorityChip.js";
import { StatusBadge } from "./StatusBadge.js";

interface Props {
  visible: CustomerSafeTicket[];
  groups: TicketGroup[] | null;
  activeKey: string | null;
  onSelect: (key: string) => void;
  /** Bereits besprochene Tickets (Präsentationsmodus, `useReviewedStore`). */
  reviewedKeys?: ReadonlySet<string>;
  /** „Neu seit letztem Termin" — s. `isNewSinceLastMeeting`. */
  newKeys?: ReadonlySet<string>;
  /** Ohne Handler kein Haken: die Zeile bleibt dann reine Navigation. */
  onToggleReviewed?: (key: string) => void;
}

function Row({
  ticket,
  active,
  onSelect,
  activeRef,
  reviewed,
  isNew,
  onToggleReviewed,
}: {
  ticket: CustomerSafeTicket;
  active: boolean;
  onSelect: () => void;
  activeRef?: Ref<HTMLButtonElement>;
  reviewed: boolean;
  isNew: boolean;
  onToggleReviewed?: (key: string) => void;
}) {
  const { t } = useTranslation();
  const waiting = ticket.status === WAITING_STATUS;
  return (
    // Der Haken steht NEBEN der Zeilenschaltfläche, nicht darin: eine
    // Schaltfläche in einer Schaltfläche ist ungültiges Markup und der Klick
    // landete unweigerlich auf der falschen von beiden.
    <Group
      gap={0}
      wrap="nowrap"
      style={{
        borderLeft: `4px solid ${waiting ? "var(--mantine-color-yellow-5)" : active ? "var(--mantine-color-orange-6)" : "transparent"}`,
        background: active ? "var(--msc-flaeche-orange)" : undefined,
      }}
    >
      {onToggleReviewed && (
        <ActionIcon
          variant="subtle"
          size="sm"
          ml={6}
          color={reviewed ? "green" : "gray"}
          aria-label={`${t("pages.customerView.present.reviewedToggle")}: ${ticket.key}`}
          aria-pressed={reviewed}
          onClick={() => onToggleReviewed(ticket.key)}
        >
          {reviewed ? "✓" : "○"}
        </ActionIcon>
      )}
      <UnstyledButton
        ref={activeRef}
        onClick={onSelect}
        px="sm"
        py={8}
        aria-current={active ? "true" : undefined}
        style={{ flex: 1, minWidth: 0 }}
      >
        <Stack gap={4}>
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Group gap={6} wrap="nowrap">
              <Text size="xs" c="dimmed" ff="monospace">{ticket.key}</Text>
              <PriorityChip name={ticket.priority?.name} />
            </Group>
            <StatusBadge status={ticket.status} category={ticket.statusCategory} />
          </Group>
          <Text size="sm" lineClamp={2}>{ticket.title}</Text>
          <Group gap={8} wrap="wrap">
            {isNew && <Badge size="xs" color="orange" variant="light">{t("pages.customerView.present.new")}</Badge>}
            {ticket.steering && (
              <Tooltip label={t("pages.customerView.steeringHint")} withArrow>
                <Badge size="xs" color="gray" variant="light">{t("pages.customerView.steering")}</Badge>
              </Tooltip>
            )}
            <AssigneeBadge name={ticket.assigneeName} />
            {ticket.reporterName && <Text size="xs" c="dimmed">{t("pages.customerView.cockpit.by", { name: ticket.reporterName })}</Text>}
            <Text size="xs" c="dimmed" title={absoluteDate(ticket.updatedIso)}>
              {t("pages.customerView.cockpit.updated", { when: relativeTime(ticket.updatedIso) })}
            </Text>
            {ticket.statusCategory !== "done" && (
              <Text size="xs" c="dimmed" title={absoluteDate(ticket.createdIso)}>
                {t("pages.customerView.cockpit.openFor", { days: daysOpen(ticket.createdIso) })}
              </Text>
            )}
            {waiting && <Badge size="xs" color="yellow" variant="light">{t("pages.customerView.cockpit.waitingOnCustomer")}</Badge>}
          </Group>
        </Stack>
      </UnstyledButton>
    </Group>
  );
}

/** Flache oder gruppierte Liste; die aktive Zeile ist markiert. */
export function TicketList({ visible, groups, activeKey, onSelect, reviewedKeys, newKeys, onToggleReviewed }: Props) {
  const { t } = useTranslation();
  const activeRowRef = useRef<HTMLButtonElement>(null);

  // Aktive Zeile im Blick halten — nur bei Änderung von `activeKey`, kein
  // Scroll beim ersten Render ohne Auswahl (dann ist die Ref noch nicht
  // gesetzt, weil keine Zeile `active` ist).
  useEffect(() => {
    activeRowRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeKey]);

  if (visible.length === 0) {
    return <Text size="sm" c="dimmed" p="md">{t("pages.customerView.cockpit.noTickets")}</Text>;
  }
  const rows = (items: CustomerSafeTicket[]) =>
    items.map((tk) => (
      <Row
        key={tk.key}
        ticket={tk}
        active={tk.key === activeKey}
        onSelect={() => onSelect(tk.key)}
        reviewed={reviewedKeys?.has(tk.key) === true}
        isNew={newKeys?.has(tk.key) === true}
        onToggleReviewed={onToggleReviewed}
        activeRef={tk.key === activeKey ? activeRowRef : undefined}
      />
    ));

  if (!groups) return <Stack gap={0}>{rows(visible)}</Stack>;
  return (
    <Stack gap={0}>
      {groups.map((g) => (
        <Box key={g.label}>
          <Group justify="space-between" px="sm" py={6} bg="var(--msc-flaeche)">
            <Text size="xs" fw={600}>{g.label}</Text>
            <Badge size="xs" variant="light" color="gray">{g.tickets.length}</Badge>
          </Group>
          {rows(g.tickets)}
        </Box>
      ))}
    </Stack>
  );
}
